import { useDeferredValue, useState } from "react"

import { useQuery } from "@tanstack/react-query"

import type { DirectorySearchResult } from "@/services/directory-search-service"

import { queryKeys } from "@/lib/query-keys"
import { DirectorySearchService } from "@/services/directory-search-service"

type UseDirectorySearchOptions = {
  /** Entity types to search. Defaults to ["users", "groups"] */
  types?: ("users" | "groups")[]
  /** Maximum results to return. Defaults to 10 */
  top?: number
  /** Minimum query length before searching. Defaults to 2 */
  minQueryLength?: number
  /** Debounce delay in ms. Using deferred value for better UX */
  enabled?: boolean
  /** Send admin=true to bypass group search restrictions */
  admin?: boolean
  /** Scope user results to transitive members of this Azure AD group. */
  scopeGroup?: string
  /** Drop guests and disabled accounts from the user results. */
  membersOnly?: boolean
}

type UseDirectorySearchReturn = {
  /** Current search query */
  query: string
  /** Set the search query */
  setQuery: (query: string) => void
  /** Search results */
  results: DirectorySearchResult[]
  /** Loading state */
  isLoading: boolean
  /** Whether a search is in progress (including deferred) */
  isSearching: boolean
  /** Error if any */
  error: Error | null
  /** Clear the search */
  clear: () => void
}

/**
 * Hook for searching Azure AD directory (users and groups).
 * Uses React Query with deferred value for smooth UX.
 */
export function useDirectorySearch(
  options: UseDirectorySearchOptions = {}
): UseDirectorySearchReturn {
  const {
    types = ["users", "groups"],
    top = 10,
    minQueryLength = 2,
    enabled = true,
    admin,
    scopeGroup,
    membersOnly,
  } = options

  const [query, setQuery] = useState("")
  const deferredQuery = useDeferredValue(query)
  const isSearching = query !== deferredQuery

  const shouldSearch = enabled && deferredQuery.trim().length >= minQueryLength

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.directorySearch(
      deferredQuery,
      `${types.join(",")}${admin ? ":admin" : ""}${scopeGroup ? `:sg:${scopeGroup}` : ""}${membersOnly ? ":members" : ""}`
    ),
    queryFn: () =>
      DirectorySearchService.searchDirectory({
        query: deferredQuery,
        types,
        top,
        admin,
        scopeGroup,
        membersOnly,
      }),
    enabled: shouldSearch,
    staleTime: 1000 * 60 * 2, // Consider data fresh for 2 minutes
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
  })

  const clear = () => {
    setQuery("")
  }

  return {
    query,
    setQuery,
    results: data ?? [],
    isLoading: isLoading && shouldSearch,
    isSearching: isSearching || (isLoading && shouldSearch),
    error: error as Error | null,
    clear,
  }
}
